// Category names arrive from the printables API as English data. For UI
// display we map them through the printables:categoryNames catalog using a
// normalized-name key; unknown/new categories fall back to the raw English
// name, so a category added server-side can never break the UI.
export function categoryNameKey(name) {
  return String(name)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// GRADE-SHAPED CATEGORY NAMES — translated by pattern, not by catalog key.
//
// The Curriculum branch is full of generated grade labels: Common Core and NGSS
// each publish "Grade 1" … "Grade 8", plus "Grade K", "Grades 6, 7, 8" and
// "Grades 9, 10, 11, 12". Enumerating them as categoryNames keys would rot the
// moment a standard set is re-ingested with a new band, so they are matched and
// rebuilt from the SAME three keys the poster cards and the curriculum document
// chips already use (grade.single / grade.range / grade.kindergarten). That is
// what keeps grade wording identical everywhere in the app.
//
// Anchored at the start on purpose: "7th Grade Integrated Science - NGSS" merely
// CONTAINS the word and must fall through to the catalog, not be rewritten.
const GRADE_PREFIX = /^grades?\s+(.+)$/i;

function gradeLabel(t, name) {
  const m = GRADE_PREFIX.exec(String(name).trim());
  if (!m) return null;
  // "6, 7, 8" / "9, 10, 11, 12" / "1" / "K" — anything else is not a grade list.
  const parts = m[1].split(',').map(s => s.trim()).filter(Boolean);
  if (!parts.length || !parts.every(p => /^(k|\d{1,2})$/i.test(p))) return null;
  const fmt = (p) => (/^k$/i.test(p) ? t('grade.kindergarten') : p);
  return parts.length === 1
    ? t('grade.single', { grade: fmt(parts[0]) })
    : t('grade.range', { min: fmt(parts[0]), max: fmt(parts[parts.length - 1]) });
}

// t must be bound to the 'printables' namespace.
export function categoryLabel(t, name) {
  if (!name) return name;
  const grade = gradeLabel(t, name);
  if (grade) return grade;
  const key = 'categoryNames.' + categoryNameKey(name);
  const translated = t(key, { defaultValue: '' });
  // Belt and braces: `defaultValue: ''` should make a miss falsy, but when the
  // namespace has not resolved i18next hands back the key itself, which then
  // renders verbatim ("categoryNames.use-a-model-to-explain-the-water-cycle...").
  // Posters categorised by a full standard sentence hit this on every card, so
  // treat any key-shaped result as a miss and show the English name instead.
  if (!translated || translated === key || translated.startsWith('categoryNames.')) return name;
  return translated;
}
